import type { BoardListFilter, BoardListSort, BoardSummaryDto } from '@miroom/shared';
import { create } from 'zustand';
import type { BoardsApi } from '../ports/api';

export type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

const SORT_KEY = 'miroom.dashboardSort';

function loadSort(): BoardListSort {
  try {
    return localStorage.getItem(SORT_KEY) === 'name' ? 'name' : 'date';
  } catch {
    return 'date';
  }
}

function saveSort(sort: BoardListSort) {
  try {
    localStorage.setItem(SORT_KEY, sort);
  } catch {
    // storage unavailable — the choice lives for this session only
  }
}

interface DashboardState {
  filter: BoardListFilter;
  search: string;
  /** Remembered per browser; the trash ignores it. */
  sort: BoardListSort;
  boards: BoardSummaryDto[];
  status: LoadStatus;
  error: unknown;
  requestId: number;
  setFilter(filter: BoardListFilter): void;
  setSearch(search: string): void;
  setSort(sort: BoardListSort): void;
  replaceBoard(id: string, patch: Partial<BoardSummaryDto>): void;
  removeBoard(id: string): void;
}

export const useDashboard = create<DashboardState>((set, get) => ({
  filter: 'recent',
  search: '',
  sort: loadSort(),
  boards: [],
  status: 'idle',
  error: null,
  requestId: 0,
  setFilter: (filter) => set({ filter }),
  setSearch: (search) => set({ search }),
  setSort: (sort) => {
    set({ sort });
    saveSort(sort);
  },
  replaceBoard: (id, patch) =>
    set({ boards: get().boards.map((b) => (b.id === id ? { ...b, ...patch } : b)) }),
  removeBoard: (id) => set({ boards: get().boards.filter((b) => b.id !== id) }),
}));

/** Dashboard use cases bound to a BoardsApi. Stale responses are dropped by request id. */
export function dashboardActions(api: BoardsApi) {
  const store = useDashboard;
  return {
    async load() {
      const { filter, search, sort } = store.getState();
      const requestId = store.getState().requestId + 1;
      store.setState({ requestId, status: 'loading', error: null });
      try {
        const boards = await api.list(filter, search.trim() || undefined, sort);
        if (store.getState().requestId === requestId) store.setState({ boards, status: 'ready' });
      } catch (error) {
        if (store.getState().requestId === requestId) store.setState({ status: 'error', error });
      }
    },
    async toggleStar(board: BoardSummaryDto) {
      const starred = !board.starred;
      store.getState().replaceBoard(board.id, { starred });
      try {
        await api.setStarred(board.id, starred);
        if (store.getState().filter === 'starred' && !starred)
          store.getState().removeBoard(board.id);
      } catch (e) {
        store.getState().replaceBoard(board.id, { starred: !starred });
        throw e;
      }
    },
    async rename(board: BoardSummaryDto, title: string) {
      const updated = await api.update(board.id, { title });
      store
        .getState()
        .replaceBoard(board.id, { title: updated.title, updatedAt: updated.updatedAt });
    },
    async remove(board: BoardSummaryDto) {
      await api.remove(board.id);
      store.getState().removeBoard(board.id);
    },
    async restore(board: BoardSummaryDto) {
      await api.restore(board.id);
      if (store.getState().filter === 'trash') store.getState().removeBoard(board.id);
      else await this.load();
    },
    async destroy(board: BoardSummaryDto) {
      await api.destroy(board.id);
      store.getState().removeBoard(board.id);
    },
    duplicate: (board: BoardSummaryDto) => api.duplicate(board.id),
    create: (template: string, title?: string) => api.create({ template, ...(title && { title }) }),
    importFile: (snapshot: unknown, title?: string) => api.importJson(snapshot, title),
  };
}
